<template>
    <v-container class="clublink-workspace-galaxy">
      <v-row>
        <!-- LEFT: Overview -->
        <v-col cols="12" md="7">
          <v-card class="rounded-xl elevation-12 glass mb-4 clublink-card">
            <v-card-title class="clublink-pane-header">
              <v-icon left color="#d4af37">mdi-briefcase</v-icon>
              <span class="white--text" style="font-family: 'Playfair Display', serif;">{{ product ? product.name : '...' }}</span>
              <v-spacer/>
              <div class="d-flex align-center mr-2">
                <!-- live presence -->
                <v-avatar
                  v-for="(v,i) in viewers.slice(0,5)"
                  :key="v.uid" size="24" class="mr-1"
                  :color="avatarColor(v.email || v.uid)"
                  title="Viewing now"
                >
                  <span class="white--text">{{ initials(v.email || v.uid) }}</span>
                </v-avatar>
                <v-tooltip top v-if="viewers.length">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip x-small v-bind="attrs" v-on="on" color="#d4af37" style="color: #2d4a3e;">
                      {{ viewers.length }} online
                    </v-chip>
                  </template>
                  <span>Currently viewing</span>
                </v-tooltip>
              </div>
              <v-chip
                x-small class="text-capitalize"
                :color="statusColor(product && product.status)" text-color="white"
              >
                {{ product && product.status }}
              </v-chip>
            </v-card-title>
  
            <v-card-text>
              <div class="subtitle-2 grey--text mb-2 font-weight-medium">Summary</div>
              <div class="mb-4 dark-green--text">{{ product ? product.summary : '' }}</div>
  
              <!-- KPI mini widgets -->
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-card class="kpi-card pa-3" outlined>
                    <div class="overline gold--text">Open Tasks</div>
                    <div class="title font-weight-bold dark-green--text">{{ openTaskCount }}</div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-card class="kpi-card pa-3" outlined>
                    <div class="overline gold--text">Stakeholders</div>
                    <div class="title font-weight-bold dark-green--text">{{ contacts.length }}</div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-card class="kpi-card pa-3" outlined>
                    <div class="overline gold--text">Files</div>
                    <div class="title font-weight-bold dark-green--text">{{ files.length }}</div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
  
          <!-- Quick Links -->
          <v-card class="rounded-xl elevation-6 mb-4 soft-card">
            <v-card-title class="d-flex align-center">
              <v-icon class="mr-2 gold--text">mdi-link-variant</v-icon>
              <span style="font-family: 'Playfair Display', serif; color: #2d4a3e;">Quick Links</span>
              <v-spacer/>
              <v-btn small text class="gold--text" @click="createLinkDialog=true">
                <v-icon left small>mdi-plus</v-icon> Add
              </v-btn>
            </v-card-title>
            <v-divider class="gold-divider"/>
            <v-card-text class="d-flex flex-wrap ga-2">
              <template v-if="links.length">
                <v-chip
                  v-for="l in links"
                  :key="l.id"
                  class="mr-2 mb-2 link-chip"
                  :href="l.url" target="_blank"
                  :title="l.url"
                  small
                >
                  <v-icon left small>{{ linkIcon(l.type) }}</v-icon>{{ l.label }}
                </v-chip>
              </template>
              <div v-else class="caption grey--text">No links yet.</div>
            </v-card-text>
          </v-card>
        </v-col>
  
        <!-- RIGHT: Workspace Tabs -->
        <v-col cols="12" md="5">
          <v-card class="rounded-xl elevation-12 glass clublink-card">
            <v-toolbar flat dense class="clublink-pane-header" dark>
              <v-toolbar-title class="white--text" style="font-family: 'Playfair Display', serif;">Workspace</v-toolbar-title>
              <v-spacer/>
              <v-tabs v-model="tab" background-color="transparent" dark color="#d4af37">
                <v-tab><v-icon left small>mdi-note-text-outline</v-icon>Notes</v-tab>
                <v-tab><v-icon left small>mdi-format-list-checkbox</v-icon>Tasks</v-tab>
                <v-tab><v-icon left small>mdi-account-group</v-icon>Contacts</v-tab>
                <v-tab><v-icon left small>mdi-file-document</v-icon>Files</v-tab>
                <v-tab><v-icon left small>mdi-pulse</v-icon>Activity</v-tab>
              </v-tabs>
            </v-toolbar>
  
            <v-tabs-items v-model="tab">
              <!-- NOTES -->
              <v-tab-item>
                <v-card-text>
                  <v-textarea
                    v-model="noteText" auto-grow outlined dense
                    placeholder="Add a quick note…" :rows="2"
                    class="clublink-input"
                    color="#d4af37"
                  />
                  <div class="d-flex align-center mt-2">
                    <v-switch v-model="isPrivate" inset label="Private to me" class="mr-4" color="#d4af37"/>
                    <v-spacer/>
                    <v-btn color="#d4af37" dark :loading="saving" :disabled="!noteText.trim()" @click="addNote" class="clublink-btn">
                      <v-icon left>mdi-send</v-icon> Save Note
                    </v-btn>
                  </div>
  
                  <v-divider class="my-4 gold-divider"/>
                  <div class="caption grey--text mb-2 d-flex align-center">
                    <v-icon left small class="gold--text">mdi-history</v-icon>
                    Recent notes
                    <v-spacer/>
                    <v-chip x-small class="gold-count-chip">
                      {{ visibleNotes.length }} shown
                    </v-chip>
                    <v-switch class="ml-2" hide-details dense v-model="showPrivate"
                              label="Show my private" inset color="#d4af37"/>
                  </div>
  
                  <v-skeleton-loader v-if="loadingNotes" type="list-item@4"/>
  
                  <v-list dense two-line v-else>
                    <v-list-item
                      v-for="n in visibleNotes"
                      :key="n.id"
                      class="rounded-lg mb-2 note-item"
                    >
                      <v-list-item-avatar>
                        <v-avatar size="30" :color="avatarColor(n.authorName)">
                          <span class="white--text">{{ initials(n.authorName) }}</span>
                        </v-avatar>
                      </v-list-item-avatar>
  
                      <v-list-item-content>
                        <v-list-item-title class="d-flex align-center">
                          <span class="font-weight-medium dark-green--text">{{ n.authorName }}</span>
                          <v-chip v-if="n.isPrivate" x-small class="ml-2" color="grey darken-1" text-color="white">private</v-chip>
                          <span class="caption grey--text ml-2">{{ prettyTs(n.createdAt) }}</span>
                        </v-list-item-title>
                        <v-list-item-subtitle class="pre">{{ n.text }}</v-list-item-subtitle>
                      </v-list-item-content>
  
                      <v-spacer/>
                      <v-btn
                        v-if="n.authorUid===meUid"
                        icon :loading="delId===n.id"
                        @click="delNote(n)" title="Delete"
                      >
                        <v-icon color="red">mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item>
  
                    <div v-if="!visibleNotes.length" class="pa-4 text-center grey--text">
                      <v-icon class="mb-2 gold--text">mdi-note-outline</v-icon>
                      <div>No notes yet.</div>
                    </div>
                  </v-list>
                </v-card-text>
              </v-tab-item>
  
              <!-- TASKS -->
              <v-tab-item>
                <v-card-text>
                  <v-form class="mb-2">
                    <v-text-field v-model="taskForm.title" dense outlined label="Task title" class="clublink-input" color="#d4af37"/>
                    <div class="d-flex ga-2">
                      <v-select
                        v-model="taskForm.priority" :items="['low','normal','high']"
                        dense outlined label="Priority" class="mr-2 clublink-input" style="max-width:140px"
                        color="#d4af37"
                      />
                      <v-menu ref="dueMenu" v-model="dueMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-bind="attrs" v-on="on"
                            dense outlined label="Due date" readonly :value="taskDueLabel"
                            class="mr-2 clublink-input" style="max-width:180px"
                            color="#d4af37"
                          />
                        </template>
                        <v-date-picker v-model="taskForm.dueDate" @input="dueMenu=false" color="#d4af37"></v-date-picker>
                      </v-menu>
                      <v-switch v-model="taskForm.private" inset label="Private" class="mr-2" color="#d4af37"/>
                      <v-spacer/>
                      <v-btn color="#d4af37" dark :loading="savingTask" :disabled="!taskForm.title.trim()" @click="addTask" class="clublink-btn">
                        <v-icon left>mdi-plus</v-icon> Add
                      </v-btn>
                    </div>
                  </v-form>
  
                  <div class="d-flex align-center mb-1">
                    <v-chip-group v-model="taskFilter" mandatory active-class="gold-chip-active">
                      <v-chip small value="all">All</v-chip>
                      <v-chip small value="mine">My tasks</v-chip>
                      <v-chip small value="open">Open</v-chip>
                      <v-chip small value="done">Done</v-chip>
                    </v-chip-group>
                    <v-spacer/>
                    <v-select
                      v-model="taskSort" :items="taskSortItems"
                      dense outlined hide-details style="max-width:190px"
                      class="clublink-input-small"
                      color="#d4af37"
                    />
                  </div>
  
                  <v-skeleton-loader v-if="loadingTasks" type="list-item@4"/>
                  <v-list v-else dense>
                    <v-list-item
                      v-for="t in filteredTasks"
                      :key="t.id" class="rounded-lg mb-2 task-item"
                    >
                      <v-list-item-action>
                        <v-checkbox :input-value="t.status==='done'" @change="toggleTask(t)" color="#d4af37"></v-checkbox>
                      </v-list-item-action>
  
                      <v-list-item-content>
                        <v-list-item-title :class="{'grey--text text--darken-1 text--line-through': t.status==='done'}">
                          {{ t.title }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <v-chip x-small class="text-capitalize mr-2" :color="priorityColor(t.priority)" text-color="white">
                            {{ t.priority || 'normal' }}
                          </v-chip>
                          <span v-if="t.dueAt" :class="{'red--text': isOverdue(t)}">
                            Due {{ prettyDate(t.dueAt) }}
                          </span>
                          <span v-else class="grey--text">No due date</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
  
                      <v-spacer/>
                      <v-btn v-if="t.authorUid===meUid" icon @click="deleteTask(t)">
                        <v-icon color="red">mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item>
                    <div v-if="!filteredTasks.length" class="pa-4 text-center grey--text">
                      <v-icon class="mb-2 gold--text">mdi-format-list-checkbox</v-icon>
                      <div>No tasks match your filters.</div>
                    </div>
                  </v-list>
                </v-card-text>
              </v-tab-item>
  
              <!-- CONTACTS -->
              <v-tab-item>
                <v-card-text>
                  <div class="d-flex align-center mb-2">
                    <v-text-field v-model="contactSearch" dense outlined hide-details placeholder="Search contacts…" class="clublink-input" color="#d4af37"/>
                    <v-spacer/>
                    <v-btn color="#d4af37" dark @click="contactDialog=true" class="clublink-btn ml-2">
                      <v-icon left>mdi-account-plus</v-icon> New Contact
                    </v-btn>
                  </div>
  
                  <v-skeleton-loader v-if="loadingContacts" type="list-item@4"/>
                  <v-list v-else dense two-line>
                    <v-list-item v-for="c in filteredContacts" :key="c.id" class="rounded-lg mb-2">
                      <v-list-item-avatar>
                        <v-avatar size="30" :color="avatarColor(c.name || c.email)">
                          <span class="white--text">{{ initials(c.name || c.email) }}</span>
                        </v-avatar>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="dark-green--text">{{ c.name }}</v-list-item-title>
                        <v-list-item-subtitle>
                          <span class="mr-2">{{ c.title }}</span>
                          <a :href="'mailto:'+c.email" class="gold--text">{{ c.email }}</a>
                          <span v-if="c.phone"> • {{ c.phone }}</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-chip v-if="c.role" x-small class="text-capitalize">{{ c.role }}</v-chip>
                      <v-spacer/>
                      <v-btn icon @click="removeContact(c)"><v-icon color="red">mdi-delete</v-icon></v-btn>
                    </v-list-item>
                    <div v-if="!filteredContacts.length" class="pa-4 text-center grey--text">
                      <v-icon class="mb-2 gold--text">mdi-account-search</v-icon>
                      <div>No contacts found.</div>
                    </div>
                  </v-list>
                </v-card-text>
              </v-tab-item>
  
              <!-- FILES -->
              <v-tab-item>
                <v-card-text>
                  <div class="d-flex align-center mb-2">
                    <v-file-input
                      v-model="fileToUpload" dense outlined show-size
                      prepend-icon="mdi-upload" placeholder="Upload a file…"
                      style="max-width: 360px"
                      class="clublink-input"
                      color="#d4af37"
                    />
                    <v-btn class="ml-2 clublink-btn" color="#d4af37" dark :loading="uploading" :disabled="!fileToUpload" @click="uploadFile">
                      <v-icon left>mdi-cloud-upload</v-icon> Upload
                    </v-btn>
                    <v-spacer/>
                    <v-text-field v-model="fileSearch" dense outlined hide-details placeholder="Search files…" style="max-width:220px" class="clublink-input-small" color="#d4af37"/>
                  </div>
  
                  <v-skeleton-loader v-if="loadingFiles" type="list-item@4"/>
                  <v-list dense v-else>
                    <v-list-item v-for="f in filteredFiles" :key="f.id" class="rounded-lg mb-2">
                      <v-list-item-avatar>
                        <v-icon class="gold--text">{{ fileIcon(f.mime) }}</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>
                          <a :href="f.url" target="_blank" rel="noopener" class="gold--text">{{ f.name }}</a>
                        </v-list-item-title>
                        <v-list-item-subtitle class="grey--text">
                          {{ (f.size/1024/1024).toFixed(2) }} MB • {{ prettyTs(f.uploadedAt) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-spacer/>
                      <v-btn icon @click="deleteFile(f)"><v-icon color="red">mdi-delete</v-icon></v-btn>
                    </v-list-item>
                    <div v-if="!filteredFiles.length" class="pa-4 text-center grey--text">
                      <v-icon class="mb-2 gold--text">mdi-file-document-outline</v-icon>
                      <div>No files uploaded yet.</div>
                    </div>
                  </v-list>
                </v-card-text>
              </v-tab-item>
  
              <!-- ACTIVITY -->
              <v-tab-item>
                <v-card-text>
                  <v-skeleton-loader v-if="loadingActivity" type="list-item@6"/>
                  <v-timeline v-else dense align-top>
                    <v-timeline-item
                      v-for="a in activity"
                      :key="a.id"
                      :icon="activityIcon(a.type)"
                      :color="activityColor(a.type)"
                      small
                    >
                      <div class="font-weight-medium dark-green--text">{{ a.actorName }}</div>
                      <div class="caption grey--text">{{ prettyTs(a.createdAt) }}</div>
                      <div class="mt-1">{{ a.message }}</div>
                    </v-timeline-item>
                  </v-timeline>
                  <div v-if="!activity.length" class="pa-4 text-center grey--text">
                    <v-icon class="mb-2 gold--text">mdi-pulse</v-icon>
                    <div>No activity yet.</div>
                  </div>
                </v-card-text>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
  
      <!-- CREATE CONTACT DIALOG -->
      <v-dialog v-model="contactDialog" max-width="520">
        <v-card class="rounded-xl clublink-dialog">
          <v-card-title class="clublink-dialog-header">
            <v-icon left color="#d4af37">mdi-account-plus</v-icon>
            <span style="font-family: 'Playfair Display', serif;">New Contact</span>
          </v-card-title>
          <v-divider class="gold-divider"/>
          <v-card-text class="pa-6">
            <v-text-field v-model="contactForm.name" label="Full name" dense outlined class="clublink-input" color="#d4af37"/>
            <v-text-field v-model="contactForm.title" label="Title" dense outlined class="clublink-input" color="#d4af37"/>
            <v-text-field v-model="contactForm.email" label="Email" type="email" dense outlined class="clublink-input" color="#d4af37"/>
            <v-text-field v-model="contactForm.phone" label="Phone" dense outlined class="clublink-input" color="#d4af37"/>
            <v-select v-model="contactForm.role" :items="['Decision Maker','Influencer','User','Procurement','Other']" label="Role" dense outlined class="clublink-input" color="#d4af37"/>
            <v-textarea v-model="contactForm.notes" label="Notes" dense outlined auto-grow class="clublink-input" color="#d4af37"/>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer/><v-btn text @click="contactDialog=false" class="gold--text">Cancel</v-btn>
            <v-btn color="#d4af37" dark :loading="savingContact" :disabled="!contactForm.name || !contactForm.email" @click="addContact" class="clublink-btn">
              <v-icon left>mdi-content-save</v-icon> Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- CREATE LINK DIALOG -->
      <v-dialog v-model="createLinkDialog" max-width="480">
        <v-card class="rounded-xl clublink-dialog">
          <v-card-title class="clublink-dialog-header">
            <v-icon left color="#d4af37">mdi-link-plus</v-icon>
            <span style="font-family: 'Playfair Display', serif;">Add Link</span>
          </v-card-title>
          <v-divider class="gold-divider"/>
          <v-card-text class="pa-6">
            <v-text-field v-model="linkForm.label" label="Label" dense outlined class="clublink-input" color="#d4af37"/>
            <v-text-field v-model="linkForm.url" label="URL" dense outlined class="clublink-input" color="#d4af37"/>
            <v-select v-model="linkForm.type" :items="['doc','sheet','deck','calendar','other']" label="Type" dense outlined class="clublink-input" color="#d4af37"/>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer/><v-btn text @click="createLinkDialog=false" class="gold--text">Cancel</v-btn>
            <v-btn color="#d4af37" dark :loading="savingLink" :disabled="!linkForm.label || !linkForm.url" @click="addLink" class="clublink-btn">
              <v-icon left>mdi-content-save</v-icon> Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Snackbar -->
      <v-snackbar v-model="snack.show" :timeout="2600" bottom right color="#2d4a3e">
        {{ snack.msg }} 
        <v-btn text @click="snack.show=false" color="#d4af37">Close</v-btn>
      </v-snackbar>
    </v-container>
  </template>
  
  <script>
  import { db, storage, auth } from '@/firebase'
  import {
    doc, getDoc, collection, addDoc, onSnapshot, query,
    serverTimestamp, deleteDoc, where, updateDoc, getDocs, limit
  } from 'firebase/firestore'
  import { ref as sRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
  
  export default {
    name: 'StaffProductDetail',
    props: { id: { type: String, required: true } },
    data () {
      return {
        tab: 0,
        product: null,
  
        // Presence
        viewers: [],
        presenceInterval: null,
  
        // NOTES
        publicNotes: [], myPrivateNotes: [],
        unsubPublic: null, unsubPrivate: null,
        loadingNotes: true, noteText: '', isPrivate: false, showPrivate: true,
        saving: false, delId: '', meUid: '', meName: '',
  
        // TASKS
        tasks: [], unsubTasks: null, loadingTasks: true,
        taskForm: { title:'', priority:'normal', dueDate:null, private:false },
        savingTask: false, taskFilter: 'all',
        taskSort: 'due-asc',
        taskSortItems: [
          { text: 'Due (soonest)', value: 'due-asc' },
          { text: 'Due (latest)', value: 'due-desc' },
          { text: 'Priority', value: 'prio' }
        ],
        dueMenu: false,
  
        // CONTACTS
        contacts: [], loadingContacts: true, unsubContacts: null,
        contactDialog: false, savingContact: false, contactSearch: '',
        contactForm: { name:'', title:'', email:'', phone:'', role:'', notes:'' },
  
        // FILES
        files: [], loadingFiles: true, unsubFiles: null,
        fileToUpload: null, uploading: false, fileSearch:'',
  
        // LINKS
        links: [], unsubLinks: null, createLinkDialog: false, savingLink:false,
        linkForm: { label:'', url:'', type:'doc' },
  
        // ACTIVITY
        activity: [], loadingActivity: true, unsubActivity: null,
  
        // UI
        snack: { show:false, msg:'' }
      }
    },
  
    async created () {
      const u = auth.currentUser
      if (!u) { this.$router.push('/'); return }
      this.meUid = u.uid
      this.meName = u.displayName || u.email || 'User'
  
      const pSnap = await getDoc(doc(db, 'products', this.id))
      if (pSnap.exists()) this.product = { id: pSnap.id, ...pSnap.data() }
  
      /* Presence heartbeat */
      this.touchPresence()
      this.presenceInterval = setInterval(this.touchPresence, 15000)
      const presQ = query(collection(db, 'products', this.id, 'presence'))
      onSnapshot(presQ, snap => {
        const now = Date.now()
        this.viewers = snap.docs
          .map(d => ({ uid:d.id, ...(d.data()||{}) }))
          .filter(v => now - (v.lastSeenMs||0) < 30000) // 30s window
      })
  
      /* NOTES: public + my private (privacy-safe) */
      const pubQ = query(collection(db, 'products', this.id, 'notes'), where('isPrivate', '==', false))
      this.unsubPublic = onSnapshot(pubQ, snap => {
        this.publicNotes = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        this.loadingNotes = false
      }, () => { this.loadingNotes=false })
  
      const myPrivQ = query(collection(db, 'products', this.id, 'notes'),
        where('authorUid','==', this.meUid),
        where('isPrivate','==', true)
      )
      this.unsubPrivate = onSnapshot(myPrivQ, snap => {
        this.myPrivateNotes = snap.docs.map(d => ({ id:d.id, ...d.data() }))
      })
  
      /* TASKS */
      const tQ = query(collection(db, 'products', this.id, 'tasks'))
      this.unsubTasks = onSnapshot(tQ, snap => {
        this.tasks = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        this.loadingTasks = false
      }, () => { this.loadingTasks=false })
  
      /* CONTACTS */
      const cQ = query(collection(db, 'products', this.id, 'contacts'))
      this.unsubContacts = onSnapshot(cQ, snap => {
        this.contacts = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        this.loadingContacts = false
      }, () => { this.loadingContacts=false })
  
      /* FILES */
      const fQ = query(collection(db, 'products', this.id, 'files'))
      this.unsubFiles = onSnapshot(fQ, snap => {
        this.files = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        this.loadingFiles = false
      }, () => { this.loadingFiles=false })
  
      /* LINKS */
      const lQ = query(collection(db, 'products', this.id, 'links'))
      this.unsubLinks = onSnapshot(lQ, snap => { this.links = snap.docs.map(d => ({ id:d.id, ...d.data() })) })
  
      /* ACTIVITY */
      const aQ = query(collection(db, 'products', this.id, 'activity'))
      this.unsubActivity = onSnapshot(aQ, snap => {
        this.activity = snap.docs.map(d => ({ id:d.id, ...d.data() }))
          .sort((a,b)=> this.toMs(b.createdAt)-this.toMs(a.createdAt))
        this.loadingActivity = false
      }, () => { this.loadingActivity=false })
    },
  
    beforeDestroy () {
      [this.unsubPublic,this.unsubPrivate,this.unsubTasks,this.unsubContacts,this.unsubFiles,this.unsubLinks,this.unsubActivity]
        .forEach(fn => { try { fn && fn() } catch(_){} })
      clearInterval(this.presenceInterval)
    },
  
    computed: {
      // NOTES
      allAllowedNotes () {
        const arr = [...this.publicNotes, ...this.myPrivateNotes]
        return arr.sort((a,b)=> this.toMs(b.createdAt) - this.toMs(a.createdAt))
      },
      visibleNotes () {
        return this.allAllowedNotes.filter(n => this.showPrivate ? true : !n.isPrivate)
      },
  
      // TASKS
      filteredTasks () {
        let arr = [...this.tasks]
        if (this.taskFilter==='mine') arr = arr.filter(t => t.authorUid===this.meUid || t.assigneeUid===this.meUid)
        if (this.taskFilter==='open') arr = arr.filter(t => (t.status||'open')!=='done')
        if (this.taskFilter==='done') arr = arr.filter(t => (t.status||'open')==='done')
  
        // hide private tasks by others
        arr = arr.filter(t => !t.private || t.authorUid===this.meUid)
  
        if (this.taskSort==='due-asc')  arr.sort((a,b)=> (this.toMs(a.dueAt)-this.toMs(b.dueAt)))
        if (this.taskSort==='due-desc') arr.sort((a,b)=> (this.toMs(b.dueAt)-this.toMs(a.dueAt)))
        if (this.taskSort==='prio')     arr.sort((a,b)=> (this.prioWeight(b.priority)-this.prioWeight(a.priority)))
        return arr
      },
      openTaskCount () { return this.tasks.filter(t => (t.status||'open')!=='done' && (!t.private || t.authorUid===this.meUid)).length },
  
      // CONTACTS
      filteredContacts () {
        const q = (this.contactSearch||'').toLowerCase()
        return this.contacts.filter(c => [c.name,c.email,c.title,c.role].join(' ').toLowerCase().includes(q))
      },
  
      // FILES
      filteredFiles () {
        const q = (this.fileSearch || '').toLowerCase()
        return this.files
          .filter(f => [f.name,f.mime].join(' ').toLowerCase().includes(q))
          .sort((a,b)=> this.toMs(b.uploadedAt)-this.toMs(a.uploadedAt))
      },
  
      taskDueLabel () {
        if (!this.taskForm.dueDate) return ''
        return new Date(this.taskForm.dueDate).toLocaleDateString()
      }
    },
  
    methods: {
      /* ---------- presence ---------- */
      async touchPresence () {
        try {
          await addDoc(collection(db, 'products', this.id, 'presence'), { _noop:true }) // ensure subcollection exists
        } catch(_) {}
        const meRef = doc(db, 'products', this.id, 'presence', this.meUid)
        await updateDoc(meRef, {
          email: this.meName, lastSeenMs: Date.now()
        }).catch(async ()=> {
          // create if missing
          await addDoc(collection(db, 'products', this.id, 'presence'), { _agent:'init' }).catch(()=>{})
          await updateDoc(meRef, { email:this.meName, lastSeenMs: Date.now() }).catch(()=>{})
        })
      },
  
      /* ---------- visuals ---------- */
      statusColor (s) { return (s||'').toLowerCase()==='active' ? '#4caf50' : (s||'').toLowerCase()==='paused' ? '#ff9800' : 'grey' },
      initials (n='') { return (n||'??').split(/\s+/).map(x=>x[0]).join('').slice(0,2).toUpperCase() },
      avatarColor (seed='') {
        const colors = ['#2d4a3e','#1a2e26','#3d5a4e','#4a6a5e','#5a7a6e','#d4af37','#c9a961','#b8963c']
        const i = Math.abs(String(seed).split('').reduce((a,c)=>a+c.charCodeAt(0),0)) % colors.length
        return colors[i]
      },
      priorityColor (p) {
        const v = (p||'normal').toLowerCase()
        if (v==='high') return 'red'
        if (v==='low') return 'grey'
        return 'blue'
      },
      prioWeight (p) { return (p==='high'?2 : p==='normal'?1 : 0) },
  
      /* ---------- time helpers ---------- */
      toDate (ts) {
        if (!ts) return null
        if (ts.toDate) return ts.toDate()
        if (ts.seconds) return new Date(ts.seconds * 1000)
        if (typeof ts === 'number') return new Date(ts)
        return null
      },
      toMs (ts) { const d = this.toDate(ts); return d ? d.getTime() : 0 },
      prettyTs (ts) {
        const d = this.toDate(ts); if (!d) return ''
        return d.toLocaleString(undefined, { month:'short', day:'2-digit', year:'numeric', hour:'numeric', minute:'2-digit' })
      },
      prettyDate (ts) {
        const d = this.toDate(ts); if (!d) return ''
        return d.toLocaleDateString()
      },
      isOverdue (t) {
        const due = this.toMs(t.dueAt); return due && due < Date.now() && (t.status||'open')!=='done'
      },
  
      /* ---------- NOTES ---------- */
      async addNote () {
        if (!this.noteText.trim()) return
        this.saving = true
        try {
          await addDoc(collection(db, 'products', this.id, 'notes'), {
            text: this.noteText.trim(),
            authorUid: this.meUid,
            authorName: this.meName,
            isPrivate: !!this.isPrivate,
            pinned: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
          this.logActivity('note.add', `${this.meName} added a ${this.isPrivate?'private ':''}note`)
          this.noteText = ''; this.isPrivate = false
        } catch (e) { this.snack = { show:true, msg:'Failed to add note.' } }
        finally { this.saving = false }
      },
      async delNote (n) {
        if (n.authorUid !== this.meUid) return
        this.delId = n.id
        try {
          await deleteDoc(doc(db, 'products', this.id, 'notes', n.id))
          this.logActivity('note.delete', `${this.meName} deleted a note`)
        } catch (e) { this.snack = { show:true, msg:'Failed to delete note.' } }
        finally { this.delId = '' }
      },
  
      /* ---------- TASKS ---------- */
      async addTask () {
        if (!this.taskForm.title.trim()) return
        this.savingTask = true
        try {
          const payload = {
            title: this.taskForm.title.trim(),
            priority: this.taskForm.priority || 'normal',
            status: 'open',
            private: !!this.taskForm.private,
            authorUid: this.meUid,
            assigneeUid: this.meUid, // default to self; extend with assignee picker later
            dueAt: this.taskForm.dueDate ? new Date(this.taskForm.dueDate).getTime() : null,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp()
          }
          await addDoc(collection(db, 'products', this.id, 'tasks'), payload)
          this.logActivity('task.add', `${this.meName} added task "${payload.title}"`)
          this.taskForm = { title:'', priority:'normal', dueDate:null, private:false }
        } catch (e) { this.snack = { show:true, msg:'Failed to add task.' } }
        finally { this.savingTask = false }
      },
      async toggleTask (t) {
        try {
          const next = (t.status||'open')==='done' ? 'open' : 'done'
          await updateDoc(doc(db, 'products', this.id, 'tasks', t.id), { status: next, updatedAt: serverTimestamp() })
          this.logActivity('task.toggle', `${this.meName} marked task "${t.title}" ${next==='done'?'done':'open'}`)
        } catch (e) { this.snack = { show:true, msg:'Failed to update task.' } }
      },
      async deleteTask (t) {
        try {
          await deleteDoc(doc(db, 'products', this.id, 'tasks', t.id))
          this.logActivity('task.delete', `${this.meName} deleted task "${t.title}"`)
        } catch (e) { this.snack = { show:true, msg:'Failed to delete task.' } }
      },
  
      /* ---------- CONTACTS ---------- */
      async addContact () {
        this.savingContact = true
        try {
          await addDoc(collection(db, 'products', this.id, 'contacts'), {
            ...this.contactForm,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp()
          })
          this.logActivity('contact.add', `${this.meName} added contact ${this.contactForm.name}`)
          this.contactForm = { name:'', title:'', email:'', phone:'', role:'', notes:'' }
          this.contactDialog = false
        } catch (e) { this.snack = { show:true, msg:'Failed to add contact.' } }
        finally { this.savingContact = false }
      },
      async removeContact (c) {
        try {
          await deleteDoc(doc(db, 'products', this.id, 'contacts', c.id))
          this.logActivity('contact.delete', `${this.meName} removed contact ${c.name}`)
        } catch (e) { this.snack = { show:true, msg:'Failed to remove contact.' } }
      },
  
      /* ---------- FILES ---------- */
      fileIcon (mime='') {
        if (mime.includes('pdf')) return 'mdi-file-pdf-box'
        if (mime.includes('image')) return 'mdi-file-image'
        if (mime.includes('sheet') || mime.includes('excel')) return 'mdi-file-excel'
        if (mime.includes('presentation') || mime.includes('powerpoint')) return 'mdi-file-powerpoint'
        if (mime.includes('zip')) return 'mdi-folder-zip'
        return 'mdi-file-document'
      },
      async uploadFile () {
        if (!this.fileToUpload) return
        this.uploading = true
        try {
          const f = this.fileToUpload
          const path = `products/${this.id}/${Date.now()}_${f.name}`
          const ref = sRef(storage, path)
          await uploadBytes(ref, f)
          const url = await getDownloadURL(ref)
          await addDoc(collection(db, 'products', this.id, 'files'), {
            name: f.name, path, url, size: f.size, mime: f.type,
            uploadedAt: serverTimestamp(), uploadedBy: this.meUid, uploadedByName: this.meName
          })
          this.logActivity('file.upload', `${this.meName} uploaded ${f.name}`)
          this.fileToUpload = null
          this.snack = { show:true, msg:'Uploaded.' }
        } catch (e) { this.snack = { show:true, msg:'Upload failed.' } }
        finally { this.uploading = false }
      },
      async deleteFile (f) {
        try {
          await deleteDoc(doc(db, 'products', this.id, 'files', f.id))
          if (f.path) { try { await deleteObject(sRef(storage, f.path)) } catch(_){} }
          this.logActivity('file.delete', `${this.meName} deleted ${f.name}`)
        } catch (e) { this.snack = { show:true, msg:'Failed to delete file.' } }
      },
  
      /* ---------- LINKS ---------- */
      linkIcon (type) {
        return type==='sheet' ? 'mdi-file-excel' :
               type==='deck' ? 'mdi-file-powerpoint' :
               type==='calendar' ? 'mdi-calendar' :
               type==='doc' ? 'mdi-file-document' : 'mdi-link-variant'
      },
      async addLink () {
        this.savingLink = true
        try {
          await addDoc(collection(db, 'products', this.id, 'links'), {
            ...this.linkForm, createdAt: serverTimestamp()
          })
          this.logActivity('link.add', `${this.meName} added link ${this.linkForm.label}`)
          this.linkForm = { label:'', url:'', type:'doc' }
          this.createLinkDialog = false
        } catch (e) { this.snack = { show:true, msg:'Failed to add link.' } }
        finally { this.savingLink = false }
      },
  
      /* ---------- ACTIVITY ---------- */
      activityIcon (t) {
        if (t && t.startsWith('note')) return 'mdi-note-text-outline'
        if (t && t.startsWith('task')) return 'mdi-format-list-checkbox'
        if (t && t.startsWith('contact')) return 'mdi-account'
        if (t && t.startsWith('file')) return 'mdi-file-document'
        if (t && t.startsWith('link')) return 'mdi-link-variant'
        return 'mdi-circle'
      },
      activityColor (t) {
        if (t && t.startsWith('note')) return '#2196f3'
        if (t && t.startsWith('task')) return '#9c27b0'
        if (t && t.startsWith('contact')) return '#4caf50'
        if (t && t.startsWith('file')) return '#d4af37'
        if (t && t.startsWith('link')) return '#00897b'
        return 'grey'
      },
      async logActivity (type, message) {
        try {
          await addDoc(collection(db, 'products', this.id, 'activity'), {
            type, message,
            actorUid: this.meUid, actorName: this.meName,
            createdAt: serverTimestamp()
          })
        } catch(_) {}
      }
    }
  }
  </script>
  
  <style scoped>
  /* ClubLink Colors */
  .gold--text { color: #d4af37 !important; }
  .dark-green--text { color: #2d4a3e !important; }
  
  /* Galaxy ambiance */
  .clublink-workspace-galaxy{
    position: relative; 
    padding-top: 8px;
    background: linear-gradient(135deg, #1a2e26 0%, #2d4a3e 50%, #1a2e26 100%);
    overflow: hidden;
    min-height: 100vh;
  }
  
  .clublink-workspace-galaxy::before{
    content:''; 
    position:absolute; 
    inset:0; 
    pointer-events:none;
    background-image:
      radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.08) 0%, transparent 50%),
      radial-gradient(2px 2px at 20% 30%, rgba(212, 175, 55, 0.6), transparent 60%),
      radial-gradient(1.5px 1.5px at 70% 10%, rgba(212, 175, 55, 0.5), transparent 60%),
      radial-gradient(1.8px 1.8px at 40% 80%, rgba(212, 175, 55, 0.4), transparent 60%);
    animation: twinkle 6s ease-in-out infinite alternate; 
    opacity:.55;
  }
  
  @keyframes twinkle{ from{opacity:.35} to{opacity:.85} }
  
  .glass{ 
    backdrop-filter: saturate(130%) blur(6px); 
    background: rgba(255,255,255,.96); 
  }
  
  .clublink-card {
    border-top: 4px solid #d4af37 !important;
    position: relative;
    overflow: hidden;
  }
  
  .clublink-card::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 100%;
    background: linear-gradient(135deg, transparent, rgba(212, 175, 55, 0.03));
    pointer-events: none;
  }
  
  .clublink-pane-header{ 
    background: linear-gradient(135deg, #2d4a3e, #1a2e26);
    color:#fff; 
  }
  
  .gold-divider {
    border-color: rgba(212, 175, 55, 0.2) !important;
    border-width: 2px !important;
  }
  
  .soft-card{ 
    border: 2px solid rgba(212, 175, 55, 0.2);
    background: rgba(212, 175, 55, 0.02);
  }
  
  .kpi-card{ 
    border-radius: 12px;
    border: 2px solid rgba(212, 175, 55, 0.2);
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.05), rgba(248, 246, 241, 0.5));
  }
  
  .link-chip {
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(201, 169, 97, 0.1)) !important;
    color: #2d4a3e !important;
    border: 1px solid rgba(212, 175, 55, 0.3);
  }
  
  .link-chip:hover {
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.25), rgba(201, 169, 97, 0.15)) !important;
  }
  
  .gold-count-chip {
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(201, 169, 97, 0.1)) !important;
    color: #2d4a3e !important;
    border: 1px solid rgba(212, 175, 55, 0.3);
  }
  
  .note-item, .task-item{ 
    transition: background .12s ease, transform .12s ease;
    background: rgba(212, 175, 55, 0.02);
  }
  
  .note-item:hover, .task-item:hover{ 
    background: rgba(212, 175, 55, 0.08);
    transform: translateY(-1px); 
  }
  
  /* Chips */
  .gold-chip-active {
    background: linear-gradient(135deg, #d4af37, #c9a961) !important;
    color: #2d4a3e !important;
  }
  
  /* Form Inputs */
  .clublink-input >>> .v-input__slot {
    background-color: #f8f6f1 !important;
    border-radius: 8px !important;
  }
  
  .clublink-input >>> fieldset {
    border-color: #e0e0e0 !important;
    border-width: 2px !important;
  }
  
  .clublink-input >>> .v-input--is-focused fieldset {
    border-color: #d4af37 !important;
  }
  
  .clublink-input-small >>> .v-input__slot {
    background-color: #f8f6f1 !important;
    border-radius: 6px !important;
  }
  
  .clublink-input-small >>> fieldset {
    border-color: #e0e0e0 !important;
  }
  
  .clublink-input-small >>> .v-input--is-focused fieldset {
    border-color: #d4af37 !important;
  }
  
  /* Buttons */
  .clublink-btn {
    font-family: 'Montserrat', sans-serif !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
  }
  
  .clublink-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(212, 175, 55, 0.4) !important;
  }
  
  /* Dialog */
  .clublink-dialog {
    border-top: 4px solid #d4af37 !important;
  }
  
  .clublink-dialog-header {
    background: linear-gradient(135deg, #2d4a3e, #1a2e26) !important;
    color: white;
  }
  
  .pre { white-space: pre-wrap; }
  .rounded-xl{ border-radius:16px; }
  </style>